<script setup lang="ts">
import {useRoute} from 'vue-router';
import {Laboratory} from "@/core/interfaces/laboratory/Laboratory";
import useNotificationSequence from "@/notification-sequences/composables/useNotificationSequence";
import useLaboratoryColor from "@/laboratory/composables/useLaboratoryColor";
import {ref, watch} from "vue";
import {notificationSequenceApi} from "@/container";
import NotificationSequenceTimeline
    from "@/notification-sequences/components/notification-sequence-timeline/NotificationSequenceTimeline.vue";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/core/components/ui/tabs";
import NotificationSequenceTargetingDatatable
    from "@/notification-sequences/components/notification-sequence-targeting/NotificationSequenceTargetingDatatable.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import NotificationSequenceInstancesDatatable
    from "@/notification-sequences/components/notification-sequence-instances/NotificationSequenceInstancesDatatable.vue";

const props = defineProps<{
    laboratory: Laboratory;
}>();

const route = useRoute();
const sequenceId = route.params.sequenceId as string;

const {data: sequence, isLoading} = useNotificationSequence(sequenceId);
const {primaryWithOpacity, primaryColor} = useLaboratoryColor({
    primary: props.laboratory.branding?.primary_color
});

const enabled = ref<boolean | undefined>(sequence.value?.is_enabled ?? false);

watch(() => sequence.value, (newValue) => {
    enabled.value = newValue?.is_enabled;
});

/**
 * Toggle the enabled state of the notification sequence
 *
 * @param value
 */
const toggleEnabled = async (value: boolean | null) => {
    if (value === null) {
        return;
    }

    enabled.value = value;
    try {
        await notificationSequenceApi()
            .toggleEnabled(Number(sequenceId), value)
            .then((response) => {
                enabled.value = response.enabled;
            });
    } catch {
        enabled.value = sequence.value?.is_enabled;
    }
};
</script>

<template>
  <div class="p-6">
    <div v-if="isLoading || !sequence">
      <BaseFlexSpinner />
    </div>
    <div v-else class="space-y-6">
      <div class="bg-white shadow rounded-lg">
        <div class="p-4">
          <div class="flex items-center">
            <h1>Animez vos patients {{ !!sequence ? `> ${sequence.name}` : '' }}</h1>
          </div>
          <p>{{ sequence?.description }}</p>
        </div>
        <div
          class="flex flex-col items-center justify-center py-3"
          :style="{
            backgroundColor: primaryWithOpacity,
          }"
        >
          <v-switch
            :model-value="enabled"
            :color="primaryColor"
            hide-details
            @update:model-value="toggleEnabled"
          >
            <template #prepend>
              <p class="text-lg">
                Activer la séquence SMS automatique
              </p>
            </template>
          </v-switch>
          <p
            :style="{
              color: primaryColor,
            }"
          >
            <strong>Gagnez du temps !</strong> En activant la séquence automatique, tous les patients dont le numéro de téléphone est
            renseigné recevront automatiquement un SMS le jour de leur achat.
          </p>
        </div>
        <div>
          <NotificationSequenceTimeline class="p-4" :sequence="sequence" :color="laboratory?.branding?.primary_color" />
        </div>
      </div>
      <div class="bg-white shadow rounded-lg p-4">
        <Tabs default-value="targeting" class="w-full">
          <TabsList class="grid grid-cols-2">
            <TabsTrigger value="targeting">
              Ciblage patients
            </TabsTrigger>
            <TabsTrigger value="instances">
              Séquences patients
            </TabsTrigger>
          </TabsList>
          <TabsContent value="targeting">
            <NotificationSequenceTargetingDatatable :sequence="sequence" :color="primaryColor" />
          </TabsContent>
          <TabsContent value="instances">
            <NotificationSequenceInstancesDatatable :sequence="sequence" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  </div>
</template>
